/**
 * The small shared pieces. Deliberately plain: an admin console earns its keep
 * by being legible and predictable, not by being designed.
 */

import { useState, type ReactNode } from 'react';
import { ApiError } from '../api/client.ts';

export function Banner({ kind, children }: { kind: 'error' | 'warn' | 'ok'; children: ReactNode }) {
  return (
    <div className={`banner banner--${kind}`} role={kind === 'error' ? 'alert' : undefined}>
      {children}
    </div>
  );
}

/**
 * An error, shown with the server's own words.
 *
 * A 403 is called out separately because it means something an operator can act
 * on — wrong role, ask someone else — rather than something being broken. Left
 * as a generic failure it reads as a bug and gets reported as one.
 */
export function ErrorBanner({ error }: { error: unknown }) {
  if (!error) return null;
  const api = error instanceof ApiError ? error : null;
  return (
    <Banner kind="error">
      {api?.isForbidden ? <strong>Your role does not permit this. </strong> : null}
      {api ? api.message : 'Something went wrong.'}
    </Banner>
  );
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return <p className="muted">{label}</p>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="muted empty">{children}</p>;
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {hint ? <span className="field__hint">{hint}</span> : null}
    </label>
  );
}

export function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="row">
      <span className="row__label">{label}</span>
      <span className="row__value">{children}</span>
    </div>
  );
}

export function Status({ value }: { value: string }) {
  return <span className={`status status--${value}`}>{value.replace(/_/g, ' ')}</span>;
}

/**
 * A copyable identifier.
 *
 * Full value in the DOM and in the clipboard, shortened on screen — an operator
 * quoting an id into a ticket needs all of it, and reading 36 characters off a
 * screen is where transcription errors come from.
 */
export function Id({ value }: { value: string | null }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="muted">—</span>;
  return (
    <button
      type="button"
      className="idchip"
      title={value}
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      <code>{value.slice(0, 8)}</code>
      <span className="idchip__hint">{copied ? 'copied' : 'copy'}</span>
    </button>
  );
}

/**
 * A button that asks first.
 *
 * Used for the writes that are hard or impossible to walk back — posting a
 * bank leg, redirecting where every future deposit is sent. The confirmation
 * text is a required prop rather than a default, because the useful question is
 * always specific ("have you seen the money leave?") and a generic "Are you
 * sure?" trains people to click through it.
 */
export function ConfirmButton({
  className = 'button',
  confirm,
  onConfirm,
  disabled,
  children,
}: {
  className?: string;
  confirm: string;
  onConfirm: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  const [asking, setAsking] = useState(false);

  if (!asking) {
    return (
      <button type="button" className={className} disabled={disabled} onClick={() => setAsking(true)}>
        {children}
      </button>
    );
  }

  return (
    <span className="confirm">
      <span className="confirm__question">{confirm}</span>
      <button
        type="button"
        className={className}
        disabled={disabled}
        onClick={() => {
          setAsking(false);
          onConfirm();
        }}
      >
        Yes
      </button>
      <button type="button" className="button button--ghost" onClick={() => setAsking(false)}>
        Cancel
      </button>
    </span>
  );
}

/** A reason-and-submit pair, for the transitions the API requires words for. */
export function ReasonForm({
  action,
  placeholder,
  danger,
  disabled,
  onSubmit,
}: {
  action: string;
  placeholder: string;
  danger?: boolean;
  disabled?: boolean;
  onSubmit: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const ready = reason.trim().length > 0;

  return (
    <div className="reasonform">
      <input
        className="input"
        placeholder={placeholder}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        disabled={disabled}
      />
      <button
        type="button"
        className={`button ${danger ? 'button--danger' : ''}`}
        // The API rejects an empty reason, so the button is disabled until
        // there is one. A rejection with no stated reason is also the thing a
        // player will ask about first, and "none given" is not an answer.
        disabled={disabled || !ready}
        onClick={() => onSubmit(reason.trim())}
      >
        {action}
      </button>
    </div>
  );
}
